import { createStore } from '../lib/store';
import { CHILD_PATH, parsePath, pathFor, resolve, type Place, type Placeable } from './routes';
import type { Catalog, Character, Commission, Video } from '../api/types';

export type Screen =
  | 'boot'
  | 'signin'
  | 'gallery'
  | 'candidates'
  | 'name'
  | 'summary'
  | 'commission'
  | 'library'
  | 'watch';

/** What the wizard is building. Carries the spend key's inputs. */
export type Entry = {
  /** Path A. Server-returned identifier, so it is a legal spend key (R4). */
  starterSlug?: string;
  label: string;
  /** Path B. The gate's canonical — the only stable key for freetext (R4). */
  canonical?: string;
  /** Starter description; the compat fallback for today's POST /characters. */
  desc?: string;
  portraitUrl?: string | null;
  /** Which tier answered the entry itself, for the dev panel. */
  tier: 'catalog' | 'device' | 'pool' | 'generated';
};

/**
 * The app could not start. `configFault` is not decoration: the panel that
 * renders this used to tell every boot failure to go and look at /config.json,
 * including the ones that came from Entra refusing a sign-in — which sent
 * whoever was debugging to a file that was perfectly correct, while the real
 * cause (a cancelled redirect, a scope the registration does not expose) went
 * unnamed. Only loadConfig() knows it is a config fault, so only it says so.
 */
export type BootError = { message: string; configFault: boolean };

export type AppState = {
  screen: Screen;
  bootError: BootError | null;
  signedInAs: string | null;
  /**
   * Why the parent is looking at the sign-in screen rather than the gallery,
   * when there is a reason worth saying. Entra's own sentence after a refused
   * redirect, or "your session ended" after a token could not be renewed. Null
   * on a plain cold visit — nobody needs to be told they are signed out when
   * they have not tried to do anything yet.
   */
  signInNotice: string | null;

  catalog: Catalog | null;
  catalogError: string | null;

  entry: Entry | null;
  characterId: string | null;
  character: Character | null;
  chosenIndex: number | null;
  characterName: string;

  commissionId: string | null;
  commission: Commission | null;

  videos: Video[];
  /**
   * The film /watch is showing, as an ID rather than as the Video row.
   *
   * It used to hold the whole object, which meant the watch screen could only
   * ever be reached from a tile on the library — the one place that had a row
   * to hand it. Now that the screen has a URL of its own, that URL carries the
   * id and this field is filled from it on a cold load.
   *
   * NO EXTRA REQUEST, which is the part worth knowing before anyone
   * "fixes" this by fetching the row: Watch.tsx never read anything off the
   * Video except `videoId`, because the only call it makes is
   * POST /videos/{id}/playback-urls. There is no GET /videos/{id} on the API to
   * fetch one with anyway — azure/functions/function_app.py's table has the
   * list, the approval subresource and playback-urls, and nothing else.
   */
  watchingId: string | null;

  /**
   * Child mode is a MODE, not a screen: the whole shell changes, and it must
   * survive navigation rather than being one more entry in the Screen union
   * that any `go()` could quietly leave.
   *
   * childPin guards the way BACK to parent mode. It is a boundary against a
   * two-year-old, not against an attacker holding the device — but it has to
   * clear that bar, and held in memory alone it did not. See CHILD_LOCK_KEY.
   *
   * Set these through enterChildMode()/leaveChildMode(), never with a bare
   * app.set(): the store and the stored lock have to move together.
   */
  childMode: boolean;
  childPin: string | null;

  banner: string | null;

  /**
   * Why the household could not be loaded, when it could not be.
   *
   * ITS OWN FIELD RATHER THAN `banner`, because a banner is per-screen and
   * go() clears it — and this is the FIRST thing a new parent hits. App.tsx
   * calls ensureFamily() on sign-in, which is the call that CREATES the
   * household on a first run; its failure used to be swallowed by
   * `.catch(() => setBalance(null, []))`, and setBalance writes to the dev
   * panel's store. So a refused bootstrap wrote a null into a panel that is
   * off by default, and the parent got an app with no explanation and no
   * account behind it.
   */
  householdError: string | null;
};

/**
 * THE HAND-OVER, WRITTEN DOWN — because in memory alone it was one swipe deep.
 *
 * childMode and childPin lived only in this store, and this store is rebuilt on
 * every page load. So a reload dropped the child straight into the full parent
 * app: the wizard that spends credits, the library with the films the parent has
 * NOT approved and the buttons that approve them, and sign out. No PIN, no
 * prompt. And a reload is not an exotic act on the device this ships to — a
 * downward swipe on a grid of pictures is iOS Safari's pull-to-refresh, and iOS
 * discards backgrounded tabs and reloads them when you come back. A two-year-old
 * does both by accident, several times an afternoon.
 *
 * sessionStorage, not localStorage, and that is the whole of the bargain. It
 * survives every reload of this tab, which is the gesture that was breaking the
 * lock. It does not survive closing the tab — and it must not, because a lock
 * that outlives the browser is a lock a parent who has forgotten four digits
 * cannot ever open. Closing a tab also takes the app off the screen rather than
 * revealing the parent side of it, so the residual hole points somewhere harmless.
 */
const CHILD_LOCK_KEY = 'showling.childLock.v1';

type ChildLock = { childMode: boolean; childPin: string | null };

function readChildLock(): ChildLock {
  try {
    const raw = sessionStorage.getItem(CHILD_LOCK_KEY);
    if (!raw) return { childMode: false, childPin: null };
    const parsed = JSON.parse(raw) as { pin?: unknown };
    const pin = typeof parsed?.pin === 'string' && parsed.pin ? parsed.pin : null;
    // A restored lock with no PIN behind it is a room with no door. Refuse it
    // and land in parent mode, which is at least a state with controls on it.
    if (!pin) return { childMode: false, childPin: null };
    return { childMode: true, childPin: pin };
  } catch {
    // Private browsing, a disabled store, or someone else's JSON under our key.
    return { childMode: false, childPin: null };
  }
}

export const app = createStore<AppState>({
  screen: 'boot',
  bootError: null,
  signedInAs: null,
  signInNotice: null,
  catalog: null,
  catalogError: null,
  entry: null,
  characterId: null,
  character: null,
  chosenIndex: null,
  characterName: '',
  commissionId: null,
  commission: null,
  videos: [],
  watchingId: null,
  // Read at module scope so the FIRST paint after a reload is already the child
  // shell. Restoring it from an effect would flash the parent app for a frame,
  // which on a screen a child is holding is a frame too many.
  ...readChildLock(),
  banner: null,
  householdError: null,
});

/** Hand the device over: lock the way back, and remember that we did. */
export function enterChildMode(pin: string): void {
  try {
    sessionStorage.setItem(CHILD_LOCK_KEY, JSON.stringify({ pin }));
  } catch {
    // The lock still holds for this page load; it just will not survive a
    // reload. Better than refusing to hand the device over at all.
  }
  app.set({ childMode: true, childPin: pin });
  // A PUSH rather than a replace, and the back gesture is what it is for: it
  // leaves one of OUR entries above the parent's, so the child's first left-edge
  // swipe pops onto something onPop() can answer instead of onto whatever the
  // tab held before this app — which we cannot answer at all, because the
  // browser unloads the document before a listener runs.
  if (canNavigate()) window.history.pushState(null, '', href(CHILD_PATH));
}

/**
 * Take it back. Called on the correct PIN, and on every path that ends the
 * session — a signed-out parent app is not something a PIN can usefully guard,
 * and leaving the lock written would strand the next visit behind it.
 */
export function leaveChildMode(): void {
  try {
    sessionStorage.removeItem(CHILD_LOCK_KEY);
  } catch {
    /* nothing was written; nothing to remove */
  }
  app.set({ childMode: false, childPin: null });
  // The address bar said /child a moment ago and has to stop saying so in the
  // same tick. Every caller goes somewhere else immediately afterwards — the PIN
  // pad to the gallery, sign-out to sign-in — but a repaint in between must not
  // show a parent shell under a child's URL.
  if (canNavigate()) window.history.replaceState(null, '', href(pathNow()));
}

/**
 * Every step of making a character, paid or not.
 *
 * NOT the same question as the paid door in state/routes.ts, which is narrower
 * on purpose: that one answers "may a back gesture drop a parent here after
 * they have paid". This one answers "is the parent part-way through making
 * something", which is what the top bar's "start over" needs — it offers to
 * throw the current character away, and on the library there is no current
 * character to throw.
 */
export const WIZARD_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'gallery', 'candidates', 'name', 'summary', 'commission',
]);

/* ---------------------------------------------------------------- the URL
 *
 * BACK USED TO LAND ON "WHO DOES YOUR KID LOVE?", from anywhere.
 *
 * Nothing in this app had ever touched history. Every screen was one
 * `app.set({screen})` inside a SINGLE history entry, so the browser's back —
 * which on the iPad this is mostly used on is a left-edge swipe, not a button
 * anyone has to aim at — had only the entry BEFORE the app to go to. It
 * reloaded the page, boot() found a cached account and sent the parent to the
 * gallery, and the film they had just paid for was nowhere: not on screen, not
 * in the store, and (before the shelf learned about in-flight films) not in the
 * library either. Jamie hit this within a minute of ordering one.
 *
 * THE FIRST FIX WAS HALF OF ONE, and this is the other half. It pushed a stack
 * of entries, wrote the screen into `history.state`, and left the URL reading
 * `/` for all nine screens — so the gesture worked and nothing else did: a
 * refresh still landed on the gallery, a bookmark could only ever mean the
 * gallery, and a link was not worth pasting. Jamie: "I don't necessarily want
 * an SPA on the web/family site, hitting back seems broken. Having pages/URIs
 * is okay."
 *
 * SO THE URL IS THE STATE NOW, for navigation. state/routes.ts maps the two
 * directions and holds the guard; this file is the window plumbing, and the
 * rules it follows are:
 *
 *   * the URL is DERIVED, never assigned. pathNow() is the single answer to
 *     "what should the address bar say", and every write goes through it, so
 *     the URL and the screen cannot drift apart. state/nav.test.tsx asserts
 *     that for every screen, and deadends.test.ts refuses any other module the
 *     right to set `screen` at all.
 *   * every arrival — cold load, back, forward, pasted link — is parsed and
 *     then passed through resolve(), which is where the paid door and the
 *     mid-wizard redirect live.
 *   * child mode never consults a URL. See onPop().
 *
 * THREE THINGS WENT AWAY WITH THE OLD SCHEME AND ARE WORTH NAMING, so nobody
 * puts them back:
 *
 *   the `showling.nav` state tag — entries had to carry the screen because the
 *     URL could not. The path carries it now, and an entry we did not write
 *     parses like any other path.
 *   the depth counter — it existed only to tell a back gesture from a forward
 *     one, and only because the entry could not say where it was going. A URL
 *     says where it is going in both directions, so there is nothing to infer.
 *   the BACK table — where back went was a pure function of the screen you were
 *     on, computed because the stack could not be trusted to know. The stack
 *     knows now. Its one load-bearing row, `commission: 'library'`, survives as
 *     the paid door's destination in routes.ts, and as the ‹ library button
 *     CommissionProgress.tsx renders for the parents who never swipe.
 */

/** Whether this environment has a history to write to at all. */
function canNavigate(): boolean {
  return typeof window !== 'undefined' && Boolean(window.history);
}

/**
 * A path, plus whatever query string the visit arrived with.
 *
 * THE QUERY IS NOT OURS TO THROW AWAY, and pushState throws it away by default:
 * pushing '/library' over '/?debug=1' silently drops the flag. runtime.ts reads
 * ?debug=1 once at module scope, so the dev panel would stay open for the rest
 * of that page load and then vanish on the next reload — the state got lost
 * somewhere between two screens with nothing on screen to say so. Under the old
 * scheme the URL never changed at all, so the flag simply could not be lost;
 * giving screens real paths is what put it at risk.
 *
 * Nothing READS the query back: parsePath() only ever sees a pathname, and the
 * app has exactly one flag today. This carries it rather than knowing about it,
 * so a second one costs nothing.
 */
function href(path: string): string {
  return path + window.location.search;
}

/**
 * THE ONE ANSWER to "what should the address bar say right now", derived from
 * the store rather than remembered alongside it.
 */
function pathNow(): string {
  const state = app.get();
  // Boot is a phase, not a place. The path the parent asked for has to survive
  // until landOnStart() reads it, so this returns it unchanged.
  if (state.screen === 'boot') return window.location.pathname;
  // THE ORDER HERE IS App.tsx's ORDER, deliberately. It renders the sign-in
  // screen AHEAD of the child shell — a lock outliving its session guards
  // nothing, and a toddler staring at a screen whose every call 401s is worse
  // than a sign-in button they cannot use — so in that one state the URL has to
  // agree with what is painted and say /signin.
  if (state.childMode && state.screen !== 'signin') return CHILD_PATH;
  return pathFor({
    screen: state.screen,
    videoId: state.watchingId ?? undefined,
    commissionId: state.commissionId ?? undefined,
  });
}

/**
 * Arrive somewhere the guard has already approved, and make the entry we are
 * standing on say so.
 */
function land(place: Place): void {
  const patch: Partial<AppState> = { screen: place.screen, banner: null };
  // WIDENED BY A PLACE, NEVER CLEARED BY ONE. Landing on the library must not
  // drop commissionId: that id is half of the paid door, and clearing it on the
  // way past would re-open the wizard to the very next back press.
  if (place.screen === 'watch' && place.videoId) patch.watchingId = place.videoId;
  if (place.screen === 'commission' && place.commissionId) {
    patch.commissionId = place.commissionId;
  }
  app.set(patch);
  // REPLACE, not push. The entry we landed on now says what is actually on
  // screen, so backing past it a second time — or forwarding onto it — reads
  // the truth rather than the wizard step the guard just refused.
  window.history.replaceState(null, '', href(pathNow()));
}

/**
 * Where a cold load lands, and the only thing boot() has to call.
 *
 * AFTER AUTH, NEVER BEFORE. The answer to `/library` is the library for a
 * parent with a session and the sign-in screen for one without, and until
 * configureAuth() has resolved we do not know which — so this runs at the end
 * of boot() and the screen stays on `boot` until then. That ordering is also
 * what stops a cold `/new/summary` from flashing the buy button before being
 * sent to the gallery: it is never rendered at all, because the screen only
 * ever moves off `boot` to somewhere resolve() has approved.
 */
export function landOnStart(): void {
  // A LOCKED TAB DOES NOT READ ITS OWN URL, for the reason in onPop(). It still
  // needs a screen underneath the child shell: App.tsx renders the boot panel
  // ahead of everything, so leaving `screen` on 'boot' would sit a child in
  // front of "reading /config.json…" for the rest of the session.
  const asked = app.get().childMode
    ? { screen: 'gallery' as const }
    : parsePath(window.location.pathname) ?? { screen: 'gallery' as const };
  land(resolve(asked, app.get()));
}

function onPop(): void {
  if (app.get().childMode) {
    /**
     * THE LOCK, AND THE SECOND WAY OUT REAL URLS WOULD OTHERWISE BE.
     *
     * Nothing here reads the path, and that is the whole design: a URL can only
     * ever ask to LEAVE child mode, and the answer to that question is the PIN
     * pad, never the address bar. So a back gesture moves no screen and simply
     * puts our own entry back on top — the child can swipe all afternoon and
     * every swipe lands where they already were.
     *
     * The residual hole is the same one the stored lock has and points the same
     * harmless way: if the entry below ours belongs to another document the
     * browser unloads this one before any listener runs, which takes the app off
     * the screen rather than revealing the parent side of it. enterChildMode()
     * pushes an entry precisely so there is normally one of ours to consume
     * first. Typing or bookmarking a URL is not a way past this at all: that is
     * a fresh page load, and readChildLock() has decided child mode before the
     * first paint — see the module-scope block below, which rewrites the address
     * bar so a `/library` bookmark tapped by a child does not even look open.
     */
    window.history.pushState(null, '', href(pathNow()));
    return;
  }
  const asked = parsePath(window.location.pathname) ?? { screen: 'gallery' as const };
  land(resolve(asked, app.get()));
}

/**
 * Installed at MODULE SCOPE, for the same reason readChildLock() is read here:
 * the first back gesture can arrive before any React effect has run, and a
 * listener attached from a mount effect would miss it.
 *
 * The remove-then-add is for the test suite, not for the browser —
 * state/app.test.ts re-imports this module with vi.resetModules() to simulate a
 * reload, and every one of those reloads would otherwise leave a live listener
 * on the same jsdom window.
 */
type NavWindow = Window & { __showlingPop?: () => void };

if (canNavigate()) {
  const w = window as NavWindow;
  if (w.__showlingPop) window.removeEventListener('popstate', w.__showlingPop);
  w.__showlingPop = onPop;
  window.addEventListener('popstate', onPop);
  // NOTHING IS STAMPED HERE FOR A PARENT. Writing the URL now would destroy the
  // deep link before boot() has read it, and every path would resolve to the
  // gallery — which is exactly the bug this change exists to fix, reintroduced
  // one line earlier than the old one.
  //
  // A locked tab is the exception, and it cannot wait for boot: readChildLock()
  // above has already decided the child shell paints first, so an address bar
  // still reading /library would be lying from the very first frame.
  if (app.get().childMode) window.history.replaceState(null, '', href(CHILD_PATH));
}

export function go(screen: Placeable): void {
  if (!canNavigate()) {
    app.set({ screen, banner: null });
    return;
  }
  const before = window.location.pathname;
  app.set({ screen, banner: null });
  const after = pathNow();
  // ONLY A REAL CHANGE OF URL EARNS AN ENTRY. Several screens call go() with the
  // screen they are already on to clear a banner, and each of those would
  // otherwise cost the parent one press of back that did nothing. Comparing
  // paths rather than screens also covers the case a screen comparison missed:
  // a go() that changes nothing a URL can see is not a move.
  if (after !== before) window.history.pushState(null, '', href(after));
}

export function banner(message: string | null): void {
  app.set({ banner: message });
}

/** Wipe the wizard but keep the session and the catalog. */
export function resetWizard(): void {
  app.set({
    entry: null,
    characterId: null,
    character: null,
    chosenIndex: null,
    characterName: '',
    commissionId: null,
    commission: null,
    watchingId: null,
  });
}

export function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
